import React, { createContext, useCallback, useContext, useRef, useState } from 'react';

/**
 * InboxSendJobsContext — global registry for in-flight inbox reply/forward
 * sends.
 *
 * Why this exists
 * ---------------
 * The inbox reply pipeline (`useMailSend`) used to `await` the send HTTP call
 * inside `InboxView`. That meant the "Sending…" spinner disappeared and the
 * `isReplied` patch was lost the moment the user navigated away — even though
 * the actual HTTP request was still in flight in the browser. Server-side the
 * mail still went out, but the UI silently lost the result.
 *
 * By lifting job bookkeeping to the app root, the sending indicator, success
 * toast and post-send callbacks survive any route change short of a full page
 * reload. The `fetch()` itself is unaffected by React unmounts, so as long as
 * we don't tie the promise's completion to a component's lifecycle, the send
 * keeps running.
 *
 * Design
 * ------
 * - The provider owns the job array plus a `startJob` runner.
 * - Callers (`useMailSend`) pass an `execute` function that returns
 *   `{ success, message, drafted?, ... }`. The provider awaits it in a
 *   detached async IIFE and updates job status accordingly.
 * - `onSuccess` / `onError` callbacks are captured at enqueue time. If the
 *   caller's component has since unmounted, the callback may hit stale
 *   `setState`s; React 18 no-ops those safely.
 * - Successful jobs auto-dismiss after `SUCCESS_TTL_MS`. Errors stay pinned
 *   until the user dismisses them so a failure is never missed.
 */

const InboxSendJobsContext = createContext(null);

const SUCCESS_TTL_MS = 3500;
const DEFAULT_EXPECTED_MS = 12000;

export function InboxSendJobsProvider({ children }) {
  const [jobs, setJobs] = useState([]);
  const jobCounterRef = useRef(0);

  const dismissJob = useCallback((id) => {
    setJobs((prev) => prev.filter((j) => j.id !== id));
  }, []);

  const startJob = useCallback((descriptor) => {
    if (!descriptor || typeof descriptor.execute !== 'function') return null;

    jobCounterRef.current += 1;
    const id = `ivx_send_${Date.now()}_${jobCounterRef.current}`;
    const job = {
      id,
      kind: descriptor.kind || 'reply', // 'reply' | 'forward'
      recipient: descriptor.recipient || '',
      subject: descriptor.subject || '',
      senderEmail: descriptor.senderEmail || '',
      expectedDurationMs: descriptor.expectedDurationMs || DEFAULT_EXPECTED_MS,
      startedAt: Date.now(),
      status: 'sending', // 'sending' | 'success' | 'error'
      message: null,
      completedAt: null,
    };
    setJobs((prev) => [...prev, job]);

    (async () => {
      let result;
      try {
        result = await descriptor.execute();
      } catch (err) {
        console.error('[InboxSendJobs] execute threw:', err);
        result = { success: false, message: err?.message || 'Send failed.' };
      }

      const success = !!result?.success;
      const message = result?.message || (success ? 'Reply sent.' : 'Send failed.');
      const completedAt = Date.now();

      setJobs((prev) => prev.map((j) => (
        j.id === id
          ? { ...j, status: success ? 'success' : 'error', message, completedAt, result }
          : j
      )));

      try {
        if (success) descriptor.onSuccess?.(result);
        else descriptor.onError?.(result);
      } catch (cbErr) {
        // A stale callback (unmounted caller) shouldn't kill the job UI.
        console.warn('[InboxSendJobs] callback threw:', cbErr?.message);
      }

      if (success) {
        setTimeout(() => {
          setJobs((prev) => prev.filter((j) => j.id !== id));
        }, SUCCESS_TTL_MS);
      }
    })();

    return id;
  }, []);

  const value = { jobs, startJob, dismissJob };

  return (
    <InboxSendJobsContext.Provider value={value}>
      {children}
    </InboxSendJobsContext.Provider>
  );
}

/**
 * Safe hook — always returns a valid shape so callers can be rendered outside
 * the provider (e.g. tests) without crashing.
 */
export function useInboxSendJobs() {
  const ctx = useContext(InboxSendJobsContext);
  if (ctx) return ctx;
  return {
    jobs: [],
    startJob: () => null,
    dismissJob: () => {},
  };
}
